import React, { useEffect, useState } from "react";
import { FaCog } from "react-icons/fa";

const GroupList = ({ theme }) => {
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [workingId, setWorkingId] = useState(null);
  const [toast, setToast] = useState(null);
  const [dropdownId, setDropdownId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [editModal, setEditModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [programmeGroups, setProgrammeGroups] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [openProgrammesId, setOpenProgrammesId] = useState(null);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/groups?page=${page}&limit=${limit}`,
        { credentials: "include",        
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` }
         }
      );
      const { data } = await res.json();
      if (Array.isArray(data)) {
        setGroups(data);
        setFilteredGroups(data);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      setToast({
        type: "error",
        message: "Failed to load groups: " + err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchProgrammeGroups = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/pg`,
        { credentials: "include",        
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` }
        } 
      );
      const data = await res.json();
      if (Array.isArray(data)) {
        setProgrammeGroups(data);
      }
    } catch (err) {
      console.error("Failed to load programme groups:", err);
    }
  };

  const fetchProgrammes = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/programmes`,
        { credentials: "include",        
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` }
         }
      );
      const data = await res.json();
      if (Array.isArray(data)) {
        const uniqueProgrammes = data.reduce((acc, current) => {
          const x = acc.find(item => item.programme === current.programme && 
                                   item.programme_group_id === current.programme_group_id);
          if (!x && current.programme_group_id) {
            return acc.concat([current]);
          } else {
            return acc;
          }
        }, []);
        setProgrammes(uniqueProgrammes);
      }
    } catch (err) {
      console.error("Failed to load programmes:", err);
    }
  };

  const updateStatus = async (id, newStatus) => {
    setWorkingId(id);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/update-group-status/${id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json",Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: JSON.stringify({ status: newStatus }),
          credentials: "include",
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update status");

      const updated = groups.map((g) =>
        g.id === id ? { ...g, status: newStatus } : g
      );
      setGroups(updated);
      setFilteredGroups(updated);
      setToast({ type: "success", message: "Status updated successfully" });
    } catch (err) {
      setToast({ type: "error", message: err.message });
    } finally {
      setWorkingId(null);
    }
  };

const deleteGroup = async (id) => {
  setWorkingId(id);
  try {
    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/delete-groups/${id}`, // ✅ Use environment variable
      {
        method: "DELETE",
        credentials: "include",
                
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` }
      }
    );
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Delete failed");

    const updated = groups.filter((g) => g.id !== id);
    setGroups(updated);
    setFilteredGroups(updated);
    setToast({ type: "success", message: "Group deleted successfully" });
    setDeleteConfirm(null);
  } catch (err) {
    setToast({ type: "error", message: err.message });
  } finally {
    setWorkingId(null);
  }
};
  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = userData.id;

  const handleEdit = async (group) => {
    try {
      setLoading(true);
      await fetchProgrammeGroups();
      await fetchProgrammes();
      
      setEditModal({
        ...group,
        group_name: group.group_name || "",
        programme_group_id: group.programme_group_id || "",
        programme_id: group.programme_id || "",
        status: group.status ?? 1,
        updated_by: userId,
      });
      setDropdownId(null);
    } catch (err) {
      setToast({ type: "error", message: "Failed to load edit data: " + err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async () => {
    const {
      id,
      group_name,
      programme_group_id,
      programme_id,
      status,
      updated_by,
    } = editModal;

    if (!group_name || !programme_group_id) {
      setToast({
        type: "error",
        message: "Group name and Programme Group are required.",
      });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/update-groups/${id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}`  },
          body: JSON.stringify({
            group_name,
            programme_group_id,
            programme_id: programme_id || null,
            status,
            updated_by,
          }),
          credentials: "include",
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Update failed");

      const updatedGroup = {
        id,
        group_name,
        programme_group_id,
        programme_id,
        status,
      };

      const updatedGroups = groups.map((g) =>
        g.id === id ? { ...g, ...updatedGroup } : g
      );
      setGroups(updatedGroups);
      setFilteredGroups(updatedGroups);
      setToast({ type: "success", message: "Group updated successfully" });
      setEditModal(null);
    } catch (err) {
      setToast({ type: "error", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    setFilteredGroups(
      groups.filter(
        (g) =>
          g.group_name?.toLowerCase().includes(value) ||
          (g.programme_group_name?.toLowerCase().includes(value) || '') ||
          (g.programme_names?.toLowerCase().includes(value) || '')
      )
    );
  };

  const getFilteredProgrammes = () => {
    if (!editModal?.programme_group_id) return programmes;
    return programmes.filter(
      (p) => p.programme_group_id == editModal.programme_group_id
    );
  };

  useEffect(() => {
    fetchGroups();
  }, [page, limit]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  return (
    <div className="p-6 bg-white rounded-xl shadow-md">
      <h2 className="text-2xl font-semibold mb-6 text-gray-800">All Groups</h2>

      {/* Search and Limit */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearch}
          placeholder="Search by name, programme group or programmes"
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm w-full sm:w-1/3"
        />
        <div className="flex items-center text-sm">
          Show:
          <select
            value={limit}
            onChange={(e) => {
              setLimit(parseInt(e.target.value));
              setPage(1);
            }}
            className="ml-2 px-2 py-1 border border-gray-300 rounded-md"
          >
            {[10, 25, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Toast Message */}
      {toast && (
        <div
          className={`mb-4 px-4 py-2 rounded-md text-sm font-medium flex justify-between items-center ${
            toast.type === "success"
              ? "bg-green-100 text-green-800 border border-green-200"
              : "bg-red-100 text-red-800 border border-red-200"
          }`}
        >
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)}>&times;</button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Confirm Deletion</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete the group "{deleteConfirm.group_name}"?
                This action cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={workingId === deleteConfirm.id}
              >
                Cancel
              </button>
              <button
                onClick={() => deleteGroup(deleteConfirm.id)}
                disabled={workingId === deleteConfirm.id}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {workingId === deleteConfirm.id ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  "Delete Group"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-md">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase">#</th>
                <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase">Group Name</th>
                <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase">Programme Group</th>
                <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase">Programmes</th>
                <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-center font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredGroups.length > 0 ? (
                filteredGroups.map((group, i) => (
                  <React.Fragment key={group.id}>
                    <tr>
                      <td className="px-6 py-4">{(page - 1) * limit + i + 1}</td>
                      <td className="px-6 py-4">{group.group_name}</td>
                      <td className="px-6 py-4">{group.programme_group_name || "-"}</td>
                      <td className="px-6 py-4">
                        {group.programme_names.length > 0 ? (
                          <button
                            onClick={() =>
                              setOpenProgrammesId(
                                openProgrammesId === group.id ? null : group.id
                              )
                            }
                            className="text-blue-600 hover:underline"
                          >
                            View Programmes ({group.programme_names.length})
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={group.status}
                          disabled={workingId === group.id || loading}
                          onChange={(e) =>
                            updateStatus(group.id, parseInt(e.target.value))
                          }
                          className={`px-3 py-1 rounded-md text-xs font-medium border ${
                            group.status === 1
                              ? "bg-green-100 text-green-800 border-green-200"
                              : "bg-red-100 text-red-800 border-red-200"
                          }`}
                        >
                          <option value={1}>Active</option>
                          <option value={0}>Inactive</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 text-center relative">
                        <div className="inline-block">
                          <button
                            onClick={() =>
                              setDropdownId(
                                dropdownId === group.id ? null : group.id
                              )
                            }
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <FaCog />
                          </button>
                          {dropdownId === group.id && (
                            <div className="absolute right-0 mt-2 w-32 bg-white border rounded-md shadow-lg z-10">
                              <button
                                onClick={() => handleEdit(group)}
                                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(group)}
                                className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-100"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Row for Programmes */}
                    {openProgrammesId === group.id && (
                      <tr>
                        <td colSpan="6" className="bg-gray-50 px-6 py-4">
                          <div className="border border-gray-200 rounded-lg p-4 shadow-sm">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">
                              Programmes in this Group
                            </h4>
                            <ul className="space-y-2">
                              {group.programme_names.map((prog, idx) => (
                                <li
                                  key={idx}
                                  className="flex items-center text-gray-600 text-sm"
                                >
                                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                                  {prog}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    No groups found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {editModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Edit Group</h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group Name *
                </label>
                <input
                  type="text"
                  value={editModal.group_name}
                  onChange={(e) =>
                    setEditModal({ ...editModal, group_name: e.target.value })
                  }
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter group name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Programme Group *
                </label>
                <select
                  value={editModal.programme_group_id}
                  onChange={(e) =>
                    setEditModal({
                      ...editModal,
                      programme_group_id: e.target.value,
                      programme_id: "" // Reset programme when group changes
                    })
                  }
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Programme Group</option>
                  {programmeGroups.map((pg) => (
                    <option key={pg.id} value={pg.id}>
                      {pg.name}
                      {pg.university_name ? ` (${pg.university_name})` : ''}
                      {pg.residency_name ? ` - ${pg.residency_name}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={editModal.status}
                  onChange={(e) =>
                    setEditModal({
                      ...editModal,
                      status: parseInt(e.target.value),
                    })
                  }
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setEditModal(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleEditSubmit}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupList;